import themeGet from "@styled-system/theme-get";
import { get, isEmpty, filter, join } from "lodash";
import { Box, Flex, H5, P, Expandable } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";

import Panel from "src/components/Panel";
import PersonItem from "src/components/Person/PersonItem";
import * as placeholder from "src/components/Placeholder/PlaceholderStyles";
import { GroupTypesPropType } from "src/custom-prop-types/groupTypes";
import PersonPropType from "src/custom-prop-types/person";
import { copywriting } from "src/pages/PersonDetailPage/PersonDetailPage.config";
import TagsEditorContainer from "src/components/TagsEditor";
import { getSupplyBadgeGroups, HIERARCHY_SEPARATOR } from "src/util/group";
import { isValidPerson, getName, fullLegalName } from "src/util/person";
import { isTagTypeReadOnly } from "src/util/tags";

import useHandleEntityTagClick from "src/hooks/useHandleEntityTagClick";
import useRestricted from "src/app/Restricted/useRestricted";
import useUpdatePersonTagsCache from "./hooks/useUpdatePersonTagsCache";
import Bands from "./Bands";

const CustomExpandable = styled(Expandable)`
  > button {
    background: transparent;
    padding: 0;
    padding-top: ${themeGet("space.r")};
    border: 0;
    border-top: solid 1px ${themeGet("colors.greyLighter")};
    border-radius: 0;
    &:before,
    &:after {
      left: 95px;
      top: 50%;
      margin-top: 7px;
    }
    h4 {
      transition: ${themeGet("transition.transitionDefault")};
    }
    &:hover,
    &:focus {
      background: transparent;
      border: 0;
      border-top: solid 1px ${themeGet("colors.greyLighter")};
      &:before,
      &:after {
        background: ${themeGet("colors.primary")};
      }
      h4 {
        color: ${themeGet("colors.primary")};
      }
    }
  }
  > div {
    padding: 0;
    overflow: visible;
  }
`;

const DetailsWrapper = ({ closable, detailsTitle, children }) => {
  if (closable) {
    return (
      <Box mt="r">
        <CustomExpandable title={detailsTitle}>
          <Box mt="r">{children}</Box>
        </CustomExpandable>
      </Box>
    );
  }

  return (
    <Panel title={detailsTitle} data-testid="cp-person-details-panel">
      {children}
    </Panel>
  );
};

DetailsWrapper.propTypes = {
  closable: PropTypes.bool,
  detailsTitle: PropTypes.string,
  children: PropTypes.node,
};

const PlaceHolder = () => (
  <placeholder.Line
    data-testid="cp-person-details-placeholder"
    width={240}
    height={28}
  />
);

const Division = ({ division }) => {
  if (!division) {
    return null;
  }

  const { hierarchy } = division;

  const paths = join(filter(hierarchy.split(HIERARCHY_SEPARATOR)), " > ");

  return <P>{paths}</P>;
};

Division.propTypes = {
  division: PropTypes.object,
};

const PersonDetails = ({
  loading,
  person,
  groupTypes,
  tagConfig,
  closable = false,
  detailsTitle = "Details",
  user,
  workspace,
  allocationProject,
  featureFlags,
  userPerson,
  tagsVisibleIn,
}) => {
  const managingPerson = get(person, "managedBy");
  const showManagingPerson = isValidPerson(managingPerson);

  const workspaceFeatureFlags = get(workspace, "config.featureFlags", {});

  const personTagTypes = get(workspace, "config.tagTypes.person", []);
  const hideDirectReports = get(
    workspace,
    "config.allocation.hideDirectReports"
  );

  const showReportsTo = showManagingPerson && !hideDirectReports;

  const personTags = get(person, "tags", []);
  const readOnlyTagTypes = filter(personTagTypes, (tagType) => {
    return isTagTypeReadOnly({
      tagType,
      tagConfig,
      user,
    });
  });
  const isAllTagTypesReadOnly =
    readOnlyTagTypes.length === personTagTypes.length;

  const onEntityTagClick = useHandleEntityTagClick();

  const aggregateId = get(person, "aggregateId");
  const { updateTagsCache } = useUpdatePersonTagsCache(aggregateId);

  const showEmployeeId =
    workspaceFeatureFlags &&
    !workspaceFeatureFlags.hideEmployeeIdOnPersonDetails;

  const EmployedByTitle = loading ? <PlaceHolder /> : <P>{person.company}</P>;

  const DivisionDetails = loading ? (
    <PlaceHolder />
  ) : (
    <Division division={person.division} />
  );

  const ManagedByDetails = loading ? (
    <PlaceHolder />
  ) : (
    <PersonItem
      reportsTo
      isDisplayActionsButton={false}
      person={managingPerson}
      badgeGroups={getSupplyBadgeGroups({
        person: managingPerson,
        groupTypes,
      })}
      groupTypes={groupTypes}
      user={user}
      workspace={workspace}
      allocationProject={allocationProject}
      featureFlags={featureFlags}
      userPerson={userPerson}
    />
  );

  const showTagsEditor =
    !loading && aggregateId && (!isEmpty(personTags) || !isAllTagTypesReadOnly);

  const [isDirectoryUser] = useRestricted();
  const showExtra = !isDirectoryUser;
  const isViewingOwnProfile = userPerson?.aggregateId === person?.aggregateId;
  const showFullName =
    (!isDirectoryUser || isViewingOwnProfile) &&
    (person?.firstName || person?.surname || loading);

  const personBands = get(person, "bands", []);
  const hasBands = !isEmpty(personBands);

  const powerAdminOnlyFields = showExtra && (
    <>
      {showEmployeeId && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Employee_ID}
          </H5>
          {loading ? (
            <PlaceHolder />
          ) : (
            <P>{get(person, "customerEmployeeId") || "No Employee ID"}</P>
          )}
        </Box>
      )}
      {(hasBands || loading) && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Band}
          </H5>
          {loading ? (
            <PlaceHolder />
          ) : (
            <Bands person={person} loading={loading} />
          )}
        </Box>
      )}
    </>
  );

  const detailsContent = (
    <Flex gap="r" flexDirection="column">
      {showFullName && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Full_name}
          </H5>
          {loading ? <PlaceHolder /> : <P>{fullLegalName(person)}</P>}
        </Box>
      )}
      {(showReportsTo || loading) && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.REPORTS_TO}
          </H5>
          {ManagedByDetails}
        </Box>
      )}
      {(person?.company || loading) && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.EMPLOYED_BY}
          </H5>
          {EmployedByTitle}
        </Box>
      )}
      {(person?.division || loading) && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.DIVISION}
          </H5>
          {DivisionDetails}
        </Box>
      )}
      {powerAdminOnlyFields}
      {showTagsEditor && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.TAGS}
          </H5>
          <TagsEditorContainer
            entityType="person"
            entityId={aggregateId}
            entityName={getName(person)}
            readOnly={isAllTagTypesReadOnly}
            updateCache={updateTagsCache}
            onEntityTagClick={onEntityTagClick}
            comments={{}}
            tagsVisibleIn={tagsVisibleIn}
            featureFlags={workspaceFeatureFlags}
          />
        </Box>
      )}
    </Flex>
  );

  return (
    <DetailsWrapper closable={closable} detailsTitle={detailsTitle}>
      {detailsContent}
    </DetailsWrapper>
  );
};

PersonDetails.propTypes = {
  loading: PropTypes.bool,
  person: PersonPropType,
  closable: PropTypes.bool,
  detailsTitle: PropTypes.string,
  groupTypes: GroupTypesPropType,
  tagConfig: PropTypes.object,
  user: PropTypes.object,
  workspace: PropTypes.object,
  allocationProject: PropTypes.object,
  featureFlags: PropTypes.object,
  userPerson: PersonPropType,
  tagsVisibleIn: PropTypes.string,
};

export default PersonDetails;
